/* Lead Radar under a lens (docs/SERVICE_SWITCHER_PLAN.md §13 item 20).

   The radar's tables don't know about services; its lead TYPES do. This table
   mirrors server/src/radar/leadtypes.js (scripts/unit-radar-lens.mjs holds the
   two equal) and adds what the screen needs: the verb, the heat curve per type,
   the copy per lens, and the starter block a concrete lead's draft quote opens
   with. Pure, no imports — the unit test runs it alone. */

export type Lens = "fence" | "concrete";
export type RadarKind = "permit" | "sale" | "age";
export type RadarType = { id: string; label: string; plural: string; verb: string; kind: RadarKind; vertical: "fence" | "concrete" | "both"; expiryDays: number };

export const RADAR_TYPES: Record<string, RadarType> = {
  pool_permit:      { id: "pool_permit",      label: "Pool permit",       plural: "pool permits",                verb: "filed",  kind: "permit", vertical: "fence",    expiryDays: 210 },
  hot_tub_permit:   { id: "hot_tub_permit",   label: "Hot tub permit",    plural: "hot tub permits",             verb: "filed",  kind: "permit", vertical: "fence",    expiryDays: 210 },
  closing:          { id: "closing",          label: "Recent closing",    plural: "recent closings",             verb: "closed", kind: "sale",   vertical: "both",     expiryDays: 730 },
  new_construction: { id: "new_construction", label: "New construction",  plural: "new-construction homes",      verb: "built",  kind: "age",    vertical: "fence",    expiryDays: 1095 },
  concrete_permit:  { id: "concrete_permit",  label: "Concrete permit",   plural: "driveway & flatwork permits", verb: "filed",  kind: "permit", vertical: "concrete", expiryDays: 240 },
  aging_slab:       { id: "aging_slab",       label: "Settling-age home", plural: "settling-age homes",          verb: "built",  kind: "age",    vertical: "concrete", expiryDays: 1095 },
};

export const lensOf = (v: any): Lens => (v === "concrete" ? "concrete" : "fence");
export function radarType(id: any): RadarType { return RADAR_TYPES[String(id || "")] || RADAR_TYPES.pool_permit; }
/** does a lead type belong under this lens? (an unknown or legacy id is a fence lead) */
export function typeInLens(id: any, lens: any): boolean {
  const t = radarType(id);
  return t.vertical === "both" || t.vertical === lensOf(lens);
}
export function leadTypesOf(lens: any): string[] { return Object.keys(RADAR_TYPES).filter((id) => typeInLens(id, lens)); }
export const leadInLens = (lead: any, lens: any): boolean => typeInLens((lead && lead.source) || "pool_permit", lens);
export const sourceInLens = (src: any, lens: any): boolean => typeInLens((src && src.lead_type) || "pool_permit", lens);

/* ---- time ---------------------------------------------------------------- */
export function daysSince(dateStr: any, now: number = Date.now()): number {
  const t = new Date(String(dateStr || "") + (String(dateStr || "").length === 10 ? "T12:00:00" : "")).getTime();
  return Number.isFinite(t) ? Math.max(0, Math.floor((now - t) / 86400000)) : 0;
}
/** the build year of an age-type lead (permit_date is YYYY-07-01), else null */
export function builtYearOf(lead: any): number | null {
  if (!lead || radarType(lead.source).kind !== "age") return null;
  const y = Number(String(lead.permit_date || "").slice(0, 4));
  return y > 1700 ? y : null;
}

/* ---- heat: when is this lead actually ready to buy? ----------------------
   A band, never a number. Fence permits: a pool takes 60–120 days to build and
   the fence goes in LAST. Concrete permits: the slab is poured a few weeks
   after filing and cures 28 days — sealing and a garage coating go on AFTER
   that, so the window opens ~2 months out. A settling-age home is hot by its
   AGE: 18–30 years in, the driveway and walk have dropped and nobody has
   called. Closings ride a flat mid heat for either service. */
export type Heat = { score: number; days: number; label: string; tone: string };
export function heatFor(lead: any, now: number = Date.now()): Heat {
  const t = radarType(lead && lead.source);
  const days = daysSince(lead && lead.permit_date, now);
  if (t.id === "pool_permit" || t.id === "hot_tub_permit") {
    let score;
    if (days < 20) score = 0;                                      // site is active, too early
    else if (days < 60) score = ((days - 20) / 40) * 100;          // ramping
    else if (days <= 110) score = 100;                             // THE window
    else score = Math.max(0, 100 - (days - 110));                  // likely already sold
    const band = days > 110 ? { label: "Cooling", tone: "var(--fg-faint)" } : score === 100 ? { label: "Closing soon", tone: "var(--red)" } : score >= 45 ? { label: "Hot", tone: "var(--amber)" } : { label: "Warming", tone: "var(--cyan)" };
    return { score, days, ...band };
  }
  if (t.id === "concrete_permit") {
    let score;
    if (days < 30) score = 0;                                      // being poured
    else if (days < 60) score = ((days - 30) / 30) * 100;          // curing
    else if (days <= 150) score = 100;                             // seal / coat it now
    else score = Math.max(0, 100 - ((days - 150) / 90) * 100);     // fading to the 240-day expiry
    const band = days > 150 ? { label: "Cooling", tone: "var(--fg-faint)" } : score === 100 ? { label: "Seal window", tone: "var(--red)" } : score >= 45 ? { label: "Curing", tone: "var(--amber)" } : days < 30 ? { label: "Just filed", tone: "var(--fg-faint)" } : { label: "Curing", tone: "var(--cyan)" };
    return { score, days, ...band };
  }
  if (t.id === "aging_slab") {
    const yrs = days / 365.25;
    let score, band;
    if (yrs < 12) { score = 20; band = { label: "Young", tone: "var(--fg-faint)" }; }
    else if (yrs < 18) { score = 20 + ((yrs - 12) / 6) * 80; band = { label: "Settling", tone: "var(--cyan)" }; }
    else if (yrs <= 30) { score = 100; band = { label: "Prime age", tone: "var(--amber)" }; }
    else if (yrs <= 45) { score = 70; band = { label: "Older", tone: "var(--cyan)" }; }
    else { score = 40; band = { label: "Old", tone: "var(--fg-faint)" }; }
    return { score, days, ...band };
  }
  // closings and new construction are already inside their window when ingested
  return { score: 55, days, label: "Warm", tone: "var(--cyan)" };
}

/* ---- labels per lens ------------------------------------------------------- */
/** the fence footage range, or under Concrete the home's age (nothing else is a measure) */
export function estLabelFor(lead: any, lens: any, now: number = Date.now()): string | null {
  if (lensOf(lens) === "fence") {
    if (!lead || !(lead.est_fence_lf_low > 0)) return null;
    return lead.est_fence_lf_low === lead.est_fence_lf_high ? `Est. ${lead.est_fence_lf_low} LF` : `Est. ${lead.est_fence_lf_low}–${lead.est_fence_lf_high} LF`;
  }
  const y = builtYearOf(lead);
  if (!y) return null;
  const age = new Date(now).getFullYear() - y;
  return `Built ${y} · ${age} yr${age === 1 ? "" : "s"}`;
}

export const RADAR_COPY: Record<Lens, { subtitle: string; sortTitle: string; searchTitle: string; recordVerb: string; footer: string }> = {
  fence: {
    subtitle: "New pool permits, recent closings and new construction in your market — claimable leads with a draft quote one tap away, before anyone else knows their name.",
    sortTitle: "Hottest = the pool build is entering fence season (60–110 days after filing)",
    searchTitle: "Search everything your sources have ingested — recently moved/sold, pool permits, new construction — by date window (last 30 days out to 9+ months ago)",
    recordVerb: "View permit record",
    footer: "Leads come from public records — building permits, real-estate transfer returns, property-appraiser rolls (address + owner name only; no phone numbers, by design). Footage is estimated from the parcel and labeled as a range: verify on site. Compliance honesty per market: Florida §515.27 lets a pool pass final inspection with any one of four safety options — a barrier is one of them, so never pitch a fence there as \"legally required\"; Wisconsin barrier rules are municipal ordinances with their own exemptions — verify the municipality before citing code. Fair play: Kodiak Built sits on Lead Radar's permanent exclusion list, enforced in the database — it can never claim a lead.",
  },
  concrete: {
    subtitle: "Driveway and sidewalk permits, recent closings and settling-age homes in your market — claimable leads with a draft estimate one tap away, before the first call.",
    sortTitle: "Hottest = new concrete past its 28-day cure (seal or coat it now), or a home at the age its slabs have settled",
    searchTitle: "Search everything your sources have ingested — recently moved/sold, driveway & flatwork permits, settling-age homes — by date window (last 30 days out to 9+ months ago)",
    recordVerb: "View record",
    footer: "Leads come from public records — permits, deed transfers and the property-appraiser roll (address + owner name only; no phone numbers, by design). A permit or an age is a signal, never a diagnosis: nothing here says a slab is sunk until you have seen it. Pitch the walk-through and the cost of a lift against the replacement they assume. Fair play: Kodiak Built sits on Lead Radar's permanent exclusion list, enforced in the database — it can never claim a lead.",
  },
};

export type SignalOption = { id: string; label: string; sources: string[] | null; title?: string };
export function signalOptionsFor(lens: any): SignalOption[] {
  if (lensOf(lens) === "concrete") return [
    { id: "all", label: "All signals", sources: null },
    { id: "closing", label: "Recently moved / sold", sources: ["closing"] },
    { id: "permits", label: "Driveway & flatwork permits", sources: ["concrete_permit"], title: "A driveway, sidewalk, apron or slab permit — new concrete to seal after its cure, a garage slab to coat, the settled slab beside it to lift." },
    { id: "aging", label: "Settling-age homes", sources: ["aging_slab"], title: "Homes 15–30 years old from the appraiser's roll — the age the driveway and walk have settled. The neighbour-letter list." },
  ];
  return [
    { id: "all", label: "All signals", sources: null },
    { id: "closing", label: "Recently moved / sold", sources: ["closing"] },
    { id: "permits", label: "Pool / hot tub permits", sources: ["pool_permit", "hot_tub_permit"],
      title: "Both permit types in one search — in WI a hot tub install often pulls a permit (electrical/plumbing, barrier ordinances can apply); in FL portable plug-in spas usually don't, in-ground spas do." },
    { id: "new_construction", label: "New construction", sources: ["new_construction"] },
  ];
}
export const sampleLeadsFor = (lens: any, all: any[]): any[] => (all || []).filter((l) => leadInLens(l, lens));

/* ---- a concrete lead's draft estimate --------------------------------------
   The lead lands as an estimate already opened on the right job: a flatwork
   permit is new concrete to SEAL (and the sunk slab beside it to lift), a
   closing or an aging home starts on LIFTING. The surface is guessed from the
   permit's words; nothing is measured — the estimator's photo read or the
   visit sets the areas. */
export const CONCRETE_JOB_CHOICES: { id: string; label: string }[] = [
  { id: "lift", label: "Lifting & leveling" }, { id: "seal", label: "Sealing" }, { id: "coat", label: "Floor coating" }, { id: "clean", label: "Cleaning" }, { id: "caulk", label: "Caulking" },
];
export function concreteStarter(lead: any): string {
  return radarType(lead && lead.source).id === "concrete_permit" ? "seal" : "lift";
}
export function surfaceGuess(lead: any): string[] {
  const hay = [lead && lead.permit_type, lead && lead.description].filter(Boolean).join(" ").toLowerCase();
  const out: string[] = [];
  if (/drive\s?way/.test(hay)) out.push("driveway");
  if (/apron/.test(hay)) out.push("apron");
  if (/side\s?walk|walk\b|walkway/.test(hay)) out.push("walk");
  if (/patio/.test(hay)) out.push("patio");
  if (/pool\s+deck/.test(hay)) out.push("pool-deck");
  if (/step|stoop/.test(hay)) out.push("steps");
  if (/porch/.test(hay)) out.push("porch");
  if (/garage\s+(slab|floor)/.test(hay)) out.push("garage-floor");
  return out.length ? out : ["driveway"];
}
export function concreteDraftForm(lead: any, jobType: any): { blocks: any[]; scopeNotes: string; prev: any } {
  const jt = CONCRETE_JOB_CHOICES.some((c) => c.id === jobType) ? String(jobType) : concreteStarter(lead);
  const surfaces = surfaceGuess(lead);
  let block: any;
  if (jt === "coat") block = { jobType: "coat", sqft: undefined, system: "full-flake", condition: "good", rate: null };
  else if (jt === "caulk") block = { jobType: "caulk", ft: undefined, rate: null };
  else if (jt === "lift") block = { jobType: "lift", sqft: undefined, sections: undefined, drop: "unsure", surfaces, crackFt: undefined, rate: null };
  else block = { jobType: jt, sqft: undefined, surfaces, rate: null };
  // prev: what buildConcreteQuote keeps under inputs.concrete (the request's channel); the lead id rides on the quote as radarPermitId
  return { blocks: [block], scopeNotes: "", prev: { request: { heardFrom: "leadradar" } } };
}
/** the note the quote carries — `when` is the already-formatted signal date, `county` the county label */
export function radarQuoteNote(lead: any, opts: { when: string; county: string }): string {
  const t = radarType(lead && lead.source);
  const where = `(${opts.county} County, ${(lead && lead.state) || "FL"})`;
  const y = builtYearOf(lead);
  if (t.kind === "age" && y) return `Lead Radar: home built ${y} ${where} — the slabs' age is the signal, not a complaint. Nothing measured yet: a photo read or a site visit sets the areas.`;
  return `Lead Radar: ${t.label.toLowerCase()} ${(lead && lead.permit_number) || ""} ${t.verb} ${opts.when} ${where}. Nothing measured yet: a photo read or a site visit sets the areas.`.replace(/\s+/g, " ");
}
